"use strict";

exports.__esModule = true;
exports.isStaffUnavailableAtLocation = exports.isStaffChoiceRedundantAtLocation = exports.getStaffMemberLeftByLocation = exports.filterStaffMembersByLocation = void 0;
var _types = require("@wix/ambassador-bookings-services-v2-service/types");
/**
 * Narrows the staff members offered for a service to those who actually have
 * working hours at the chosen business location.
 *
 * Staff members are not associated with a location in the catalog — the association is
 * derived server-side from their WORKING_HOURS calendar events and returned as
 * `workingHoursLocationCoverage` when the `STAFF_WORKING_HOURS_LOCATIONS` conditional
 * field is requested.
 *
 * Returns an empty list when nobody works at the chosen location. That is a real answer,
 * not a failure: the visitor is told so and sent back to change location, rather than
 * being shown staff who cannot serve them. Use `isStaffUnavailableAtLocation` to detect it.
 */
const filterStaffMembersByLocation = ({
  staffMembers = [],
  selectedLocation
}) => {
  const businessLocationId = getBusinessLocationId(selectedLocation);
  if (!businessLocationId) {
    return staffMembers;
  }
  return staffMembers.filter(staffMember => isStaffAvailableAtLocation(staffMember.workingHoursLocationCoverage, businessLocationId));
};

/**
 * True when the service has staff, but none of them work at the chosen location.
 *
 * Distinguished from "this service has no staff at all", which is a pre-existing case
 * that hides the staff step altogether rather than explaining anything.
 */
exports.filterStaffMembersByLocation = filterStaffMembersByLocation;
const isStaffUnavailableAtLocation = ({
  staffMembers,
  selectedLocation
}) => !!(staffMembers != null && staffMembers.length) && filterStaffMembersByLocation({
  staffMembers,
  selectedLocation
}).length === 0;

/**
 * True when the chosen location leaves exactly one staff member.
 *
 * One option is not a choice, so the staff step is skipped, nothing offers to edit the
 * selection, and that member is simply used — the same way a service with a single staff
 * member has never shown the step. Zero available is deliberately *not* redundant: the
 * step still appears, to say so (see `isStaffUnavailableAtLocation`).
 *
 * This asks a different question from `getStaffMemberLeftByLocation` below, and the two
 * must not be collapsed into one test. Here the only thing that matters is how many
 * options are left to choose between — one is nothing to choose, however many the service
 * started with.
 */
exports.isStaffUnavailableAtLocation = isStaffUnavailableAtLocation;
const isStaffChoiceRedundantAtLocation = ({
  staffMembers,
  selectedLocation
}) => {
  // Without a business location the filter is a no-op, and whether a service with one
  // staff member shows the step is a pre-existing question owned upstream by
  // isServiceWithMultiStaff — not ours to answer.
  if (!getBusinessLocationId(selectedLocation)) {
    return false;
  }
  return filterStaffMembersByLocation({
    staffMembers,
    selectedLocation
  }).length === 1;
};

/**
 * The one staff member to *name* in place of an unavailable selection, when the location
 * is what narrowed a real choice down to one.
 *
 * Requiring more than one staff member to begin with is what makes this the location
 * filter's own doing, and it is why this is not the same test as
 * `isStaffChoiceRedundantAtLocation`. A service that simply has a single staff member has
 * a pre-existing default — "any staff", where auto-assignment is allowed — and overriding
 * that is a behaviour change nobody asked for. Naming the survivor is only right where
 * the visitor *had* a choice and the location took it away.
 */
exports.isStaffChoiceRedundantAtLocation = isStaffChoiceRedundantAtLocation;
const getStaffMemberLeftByLocation = ({
  staffMembers,
  selectedLocation
}) => {
  if (((staffMembers == null ? void 0 : staffMembers.length) ?? 0) <= 1) {
    return undefined;
  }
  const availableStaffMembers = filterStaffMembersByLocation({
    staffMembers,
    selectedLocation
  });
  return availableStaffMembers.length === 1 ? availableStaffMembers[0].staffMemberId : undefined;
};
exports.getStaffMemberLeftByLocation = getStaffMemberLeftByLocation;
const getBusinessLocationId = selectedLocation => (selectedLocation == null ? void 0 : selectedLocation.type) === _types.LocationType.BUSINESS ? selectedLocation.value : undefined;
const isStaffAvailableAtLocation = (coverage, businessLocationId) => {
  // Omitted when the enrichment was not requested (e.g. FT off on queryServices).
  if (!coverage) {
    return true;
  }
  if (coverage.availableAtAllLocations) {
    return true;
  }
  return (coverage.locationIds ?? []).includes(businessLocationId);
};
//# sourceMappingURL=filterStaffByLocation.js.map